import {
  AfterContentChecked,
  AfterContentInit,
  AfterViewChecked,
  AfterViewInit,
  DoCheck,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges
} from '@angular/core';
import {
  Observable,
  PartialObserver,
  Subject,
  Subscribable,
  Subscription
} from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { createSymbol } from '../../utils/symbol';
import {
  arrayPush,
  assertArray,
  createConsumerOnSubject,
  createGetterOnObservable,
  createSetterOnSubject,
  createSingleSubject,
  isFunction,
  isNotNil,
  isString
} from './../../../utils';
import { completeLater, unsubscribeLater } from './../../utils/rx.utils';

/**
 * Enumeration of the possible hooks
 */
const HOOK_AFTERVIEWCHECKED = 0;
const HOOK_AFTERVIEWINIT = 1;
const HOOK_AFTERCONTENTCHECKED = 2;
const HOOK_AFTERCONTENTINIT = 3;
const HOOK_DOCHECK = 4;
const HOOK_INIT = 5;
const HOOK_CHANGES = 6;
const HOOK_DESTROY = 7;

const FIELD_CALLBACKS = 1;
const FIELD_OBSERVABLE = 2;

declare type Registry = any[][];

// we hide the hook registry
const KEY_REGISTRY = createSymbol();

/**
 * Clears the registry from the component
 *
 * @param aThis     the instance
 */
function _removeRegistry(aThis: AbstractLifeCycleComponent) {
  delete aThis[KEY_REGISTRY];
}

/**
 * Makes sure we have a hook data structure for the identifier
 *
 * @param aHookIdentifier   the hook identifier
 * @param aThis     the instance
 * @return the hook structure
 */
function _assertHook(
  aHookIdentifier: number,
  aThis: AbstractLifeCycleComponent
): any[] {
  // access the registry
  return assertArray(aHookIdentifier, assertArray(KEY_REGISTRY, aThis));
}

/**
 * Makes sure we have a callbacks array defined
 *
 * @param aHook the hook structure
 * @return the callback field
 */
function _assertCallbacks(aHook: any[]): Function[] {
  // returns the callback field
  return assertArray<Function>(FIELD_CALLBACKS, aHook);
}

/**
 * Construct the desired observable
 *
 * @param aHookIdentifier   the hook
 * @param aThis  the life cycle component
 *
 * @return the observable
 */
function _createObservable(
  aHookIdentifier: number,
  aHook: any[],
  aShared: boolean,
  aThis: AbstractLifeCycleComponent
): Observable<any> {
  // construct the subject
  const subject = aShared ? createSingleSubject<any>() : new Subject<any>();
  // callbacks
  const fct: Function[] = _assertCallbacks(aHook);
  // the handlers
  const onNext = createConsumerOnSubject(subject);
  const onComplete = () => subject.complete();
  // register the hook that invokes the method
  arrayPush(onNext, fct);
  // make sure we complete in ngOnDestroy
  if (aHookIdentifier === HOOK_DESTROY) {
    // add the end handler to the same sequence
    arrayPush(onComplete, fct);
  } else {
    // add the end handler to the destroy chain
    _addHook(HOOK_DESTROY, aThis, onComplete);
  }
  // convert to a safe observable
  return subject;
}

/**
 * Registers an observable for a hook
 *
 * @param aHookIdentifier the identifier for the hook
 * @param aThis the instance of the component
 *
 * @return the observable
 */
function _getObservable(
  aHookIdentifier: number,
  aShared: boolean,
  aThis: AbstractLifeCycleComponent
): Observable<any> {
  // make sure we have the hook
  const hook: any[] = _assertHook(aHookIdentifier, aThis);
  // handle
  return (
    hook[FIELD_OBSERVABLE] ||
    (hook[FIELD_OBSERVABLE] = _createObservable(
      aHookIdentifier,
      hook,
      aShared,
      aThis
    ))
  );
}

/**
 * Registers a life cycle hook with the component
 */
function _addHook(
  aHookIdentifier: number,
  aThis: AbstractLifeCycleComponent,
  aHook: Function
) {
  // make sure we have the hook
  arrayPush(aHook, _assertCallbacks(_assertHook(aHookIdentifier, aThis)));
}

/**
 * Invokes the lifecycle hooks for the component
 *
 * @param aName     name of the hook
 * @param aObject   the registry
 * @param aThis     the this pointer
 * @param aArgs     the arguments
 */
function _invokeHooks(
  aHookIdentifier: number,
  aThis: AbstractLifeCycleComponent,
  aArgs: IArguments
): void {
  // access the registry
  const reg: Registry = aThis[KEY_REGISTRY];
  // only for an existing registry
  if (isNotNil(reg)) {
    // check if we have hooks
    const hook: any[] = reg[aHookIdentifier];
    if (isNotNil(hook)) {
      // callbacks
      const cb: Function[] = hook[FIELD_CALLBACKS];
      if (isNotNil(cb)) {
        const len = cb.length;
        for (let i = 0; i < len; i++) {
          cb[i].apply(aThis, aArgs);
        }
      }
    }
  }
}

/** Base class that allows to register life cycle hooks. This class is supposed
 * to be subclassed before use.
 *
 * The 'ngXXX' methods override the methods from the Angular life cycle interfaces. If you overridde
 * any of these methods make sure to call the super method.
 *
 * The 'onXXX' methods expose observables that will be triggered when the life cycle method occurs. This is
 * a convenient way to register hooks in the constructor of a subclass without having to override any
 * method.
 *
 * The {@link #onOnDestroy} observable is especially useful, since it can be used as a termination signal for
 * automatic unsubscriptions via the {@link http://reactivex.io/documentation/operators/takeuntil.html} operation.
 *
 * Note that hooks such as `onOnInit` and `onOnDestroy` only fire once. If you depend on such a hook in an observable
 * chain more than once make sure to share the emissions (typically via `shareReplay` )
 */
export abstract class AbstractLifeCycleComponent
  implements
    OnInit,
    OnDestroy,
    OnChanges,
    DoCheck,
    AfterContentInit,
    AfterContentChecked,
    AfterViewInit,
    AfterViewChecked {
  /**
   * @see AfterViewChecked
   * @override
   */
  ngAfterViewChecked(): void {
    _invokeHooks(HOOK_AFTERVIEWCHECKED, this, arguments);
  }

  /**
   * @see AfterViewInit
   * @override
   */
  ngAfterViewInit(): void {
    _invokeHooks(HOOK_AFTERVIEWINIT, this, arguments);
  }

  /**
   * @see AfterContentChecked
   * @override
   */
  ngAfterContentChecked(): void {
    _invokeHooks(HOOK_AFTERCONTENTCHECKED, this, arguments);
  }

  /**
   * @see AfterContentInit
   * @override
   */
  ngAfterContentInit(): void {
    _invokeHooks(HOOK_AFTERCONTENTINIT, this, arguments);
  }

  /**
   * @see DoCheck
   * @override
   */
  ngDoCheck(): void {
    _invokeHooks(HOOK_DOCHECK, this, arguments);
  }

  /**
   * @see OnChanges
   * @override
   */
  ngOnChanges(changes: SimpleChanges): void {
    _invokeHooks(HOOK_CHANGES, this, arguments);
  }

  /**
   * @see OnInit
   * @override
   */
  ngOnInit(): void {
    _invokeHooks(HOOK_INIT, this, arguments);
  }

  /**
   * @see OnDestroy
   * @override
   */
  ngOnDestroy(): void {
    // dispatch
    _invokeHooks(HOOK_DESTROY, this, arguments);
    _removeRegistry(this);
  }

  /**
   * @see AfterViewChecked
   * @return the observable representation of this callback
   */
  protected get onAfterViewChecked(): Observable<void> {
    return _getObservable(HOOK_AFTERVIEWCHECKED, false, this);
  }

  /**
   * @see AfterViewInit
   * @return the observable representation of this callback
   */
  protected get onAfterViewInit(): Observable<void> {
    return _getObservable(HOOK_AFTERVIEWINIT, true, this);
  }

  /**
   * @see AfterContentChecked
   * @return the observable representation of this callback
   */
  protected get onAfterContentChecked(): Observable<void> {
    return _getObservable(HOOK_AFTERCONTENTCHECKED, false, this);
  }

  /**
   * @see AfterContentInit
   * @return the observable representation of this callback
   */
  protected get onAfterContentInit(): Observable<void> {
    return _getObservable(HOOK_AFTERCONTENTINIT, true, this);
  }

  /**
   * @see DoCheck
   * @return the observable representation of this callback
   */
  protected get onDoCheck(): Observable<void> {
    return _getObservable(HOOK_DOCHECK, false, this);
  }

  /**
   * @see OnChanges
   * @return the observable representation of this callback
   */
  protected get onOnChanges(): Observable<SimpleChanges> {
    return _getObservable(HOOK_CHANGES, false, this);
  }

  /**
   * @see OnInit
   * @return the observable representation of this callback
   */
  protected get onOnInit(): Observable<void> {
    return _getObservable(HOOK_INIT, true, this);
  }

  /**
   * @see OnDestroy
   * @return the observable representation of this callback
   */
  protected get onOnDestroy(): Observable<void> {
    return _getObservable(HOOK_DESTROY, true, this);
  }

  /**
   * Unsubscribes the given subscription in the ngOnDestroy method. This method exists for convenience only,
   * consider to use the {@link #safeSubscribe} method instead.
   *
   * @param aSubscription the subscription to unsubscribe on
   *
   * @deprecated use `takeUntil(onOnDestroy)` instead
   */
  protected unsubscribeOnDestroy(aSubscription: Subscription): void {
    _addHook(HOOK_DESTROY, this, unsubscribeLater(aSubscription));
  }

  /***
   * Returns a property descriptor for a setter that dispatches to the given subject.
   * The subject will automatically be completed and unsubscribed on the onDestroy method. The
   * resulting descriptor may be used to define input properties in the closure of the
   * constructor of a component.
   *
   * @param aSubject  the subject
   * @return the property descriptor
   *
   * @deprecated use createSetter instead
   */
  protected describeSetter<T>(aSubject: Subject<T>): PropertyDescriptor {
    // registers the subject for completion in onDestroy
    _addHook(HOOK_DESTROY, this, completeLater(aSubject));
    // create the setter
    return createSetterOnSubject(aSubject);
  }

  /**
   * Subscribes to an observable and makes sure to clean the subscription in ngOnDestroy
   * to avoid memory leaks.
   *
   * @param aObservable   the observable to subscribe to
   * @param aObserver     the handler. If this value is a 'string', then the subscription will automatically update the respective
   *  property on the instance.
   * @param error     optional error handler
   * @param complete  optional completion handler
   *
   * @deprecated use `takeUntil(onOnDestroy)` instead
   */
  safeSubscribe<T>(
    aObservable: Subscribable<T>,
    aObserver?: PartialObserver<T> | ((value: T) => void) | string,
    error?: (error: any) => void,
    complete?: () => void
  ): void {
    // test
    let subscription;
    // test
    if (isFunction(aObserver)) {
      // connect
      subscription = aObservable.subscribe(aObserver, error, complete);
    } else if (isString(aObserver)) {
      // connect
      const _that = this;
      subscription = aObservable.subscribe(
        value => (_that[aObserver] = value),
        err => delete _that[aObserver]
      );
    } else if (isNotNil(aObserver)) {
      // must be a partial
      subscription = aObservable.subscribe(aObserver);
    } else {
      // subscribe as is
      subscription = aObservable.subscribe();
    }
    // register in onDestroy
    _addHook(HOOK_DESTROY, this, unsubscribeLater(subscription));
  }
}

/**
 * Constructs a setter that is unregistered automatically in onDestroy
 *
 * @param aSubject the subject
 * @param aThis  the component
 *
 * @return the descriptor
 */
export function createSetter<T>(
  aSubject: Subject<T>,
  aThis: AbstractLifeCycleComponent
): PropertyDescriptor {
  // registers the subject for completion in onDestroy
  _addHook(HOOK_DESTROY, aThis, completeLater(aSubject));
  // create the setter
  return createSetterOnSubject(aSubject);
}

/**
 * Constructs a getter that subscribes to a value
 *
 * @param aObservable the observable
 * @param aThis  the component
 * @param aInitial the optional initial value
 *
 * @return the descriptor
 */
export function createGetter<T>(
  aObservable: Observable<T>,
  aThis: AbstractLifeCycleComponent,
  aInitial?: T
): PropertyDescriptor {
  // the destroy hook
  const onDestroy: Observable<any> = _getObservable(HOOK_DESTROY, true, aThis);
  // create the setter
  return createGetterOnObservable(
    aObservable.pipe(takeUntil<T>(onDestroy)),
    aInitial
  );
}
